import React, { useState, useEffect } from 'react';
import { Search, Filter, Plus, BookOpen, RefreshCw, ChevronUp, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import "./Documents.css";
import toast, { Toaster } from 'react-hot-toast';
import DocumentCard from '../../components/DocumentCard';
import Button from '../../System Design/Button';
import bookService from '../../services/bookService';
import authService from '../../services/authService';
import { useLanguage } from '../../contexts/LanguageContext';

const Documents = () => {
  // État pour la recherche
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [previousPage, setPreviousPage] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState(null);
  const [mergeTitleId, setMergeTitleId] = useState(null);
  const [mergeCoverId, setMergeCoverId] = useState(null);
  const [isMerging, setIsMerging] = useState(false);
  const [mergeError, setMergeError] = useState(null);
  const [mergePreview, setMergePreview] = useState(null);
  const [mergePreviewLoading, setMergePreviewLoading] = useState(false);
  const [mergePreviewError, setMergePreviewError] = useState(null);
  const [mergeConflicts, setMergeConflicts] = useState([]);
  const [chapterChoices, setChapterChoices] = useState({});
  const [orderedChapters, setOrderedChapters] = useState([]); // Liste ordonnée des chapitres pour la fusion
  const [orderedBooks, setOrderedBooks] = useState([]); // Liste ordonnée des livres pour la fusion
  const navigate = useNavigate();
  const isAdmin = authService.isAdmin();
  const isManager = authService.isManager && authService.isManager();
  const isEmployee = !isAdmin && !isManager;

  const { t, language } = useLanguage();

  const getLocaleForLanguage = (lang) => {
    if (lang === 'en') return 'en-US';
    if (lang === 'pt') return 'pt-PT';
    return 'fr-FR';
  };

  const dateLocale = getLocaleForLanguage(language);

  // Charger les documents avec pagination et recherche
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const data = await bookService.getAllBooks({ page: currentPage });
        console.log('Données reçues de l\'API:', data);
        console.log('Résultats (data.results):', data.results);
        // L'API retourne une réponse paginée avec structure { count, next, previous, results }
        setDocuments(data.results || []);
        setTotalCount(data.count || 0);
        setNextPage(data.next);
        setPreviousPage(data.previous);
        // Calculer le nombre total de pages
        const pageSize = 10; // Django REST Framework par défaut
        setTotalPages(Math.ceil((data.count || 0) / pageSize));
      } catch (error) {
        console.error('Erreur lors du chargement des documents:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocuments();
  }, [currentPage]);

  const handleSearch = async () => {
    if (searchQuery.trim() === '') return;
    
    setIsLoading(true);
    setIsSearching(true);
    try {
      const data = await bookService.searchBooks(searchQuery, 1);
      setDocuments(data.results || []);
      setTotalCount(data.count || 0);
      setNextPage(data.next);
      setPreviousPage(data.previous);
      setCurrentPage(1);
      setTotalPages(Math.ceil((data.count || 0) / 10));
    } catch (error) {
      console.error('Erreur lors de la recherche:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const selectedDocs = documents.filter((doc) => selectedIds.includes(doc.id));

  // Gérer la recherche avec debounce
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchQuery.trim() === '') {
        // Si la recherche est vide, recharger tous les documents
        setIsSearching(false);
        const fetchDocuments = async () => {
          setIsLoading(true);
          try {
            const data = await bookService.getAllBooks();
            setDocuments(data.results || []);
            setTotalCount(data.count || 0);
            setNextPage(data.next);
            setPreviousPage(data.previous);
            setCurrentPage(1);
            setTotalPages(Math.ceil((data.count || 0) / 10));
          } catch (error) {
            console.error('Erreur lors du chargement des documents:', error);
          } finally {
            setIsLoading(false);
          }
        };
        fetchDocuments();
      } else {
        // Sinon, effectuer la recherche
        handleSearch();
      }
    }, 300); // 300ms de debounce

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  // Gestionnaires de pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleNextPage = () => {
    if (nextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Réinitialiser la recherche
  const handleResetSearch = () => {
    setSearchQuery('');
    setIsSearching(false);
    setCurrentPage(1);
  };

  // Actualiser la liste des documents
  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      const data = await bookService.getAllBooks({ page: currentPage });
      setDocuments(data.results || []);
      setTotalCount(data.count || 0);
      setNextPage(data.next);
      setPreviousPage(data.previous);
      setTotalPages(Math.ceil((data.count || 0) / 10));
    } catch (error) {
      console.error('Erreur lors de l\'actualisation des documents:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreviousPage = () => {
    if (previousPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  const toggleSelectDocument = (docId) => {
    setSelectedIds((prev) =>
      prev.includes(docId) ? prev.filter((id) => id !== docId) : [...prev, docId]
    );
  };

  const clearSelection = () => {
    setSelectedIds([]);
  };

  const handleOpenMergeDialog = async () => {
    if (selectedIds.length < 2) return;
    const firstId = selectedIds[0];
    setMergeTargetId(firstId);
    setMergeTitleId(firstId);
    setMergeCoverId(firstId);
    setMergeError(null);
    setMergePreview(null);
    setMergePreviewError(null);
    setMergeConflicts([]);
    setChapterChoices({});
    setShowMergeDialog(true);

    try {
      setMergePreviewLoading(true);
      const data = await bookService.mergeMultiplePreview(selectedIds);
      setMergePreview(data);

      const allChapters = [];
      (data.books || []).forEach((book) => {
        (book.chapters || []).forEach((ch) => {
          allChapters.push({
            id: ch.id,
            title: ch.title || '',
            bookId: book.id,
            bookTitle: book.title || '',
          });
        });
      });

      const grouped = {};
      allChapters.forEach((ch) => {
        const keyBase = (ch.title || '').trim().toLowerCase();
        const key = keyBase || `__no_title__${ch.id}`;
        if (!grouped[key]) {
          grouped[key] = {
            key,
            title: ch.title || '(Sans titre)',
            chapters: [],
          };
        }
        grouped[key].chapters.push(ch);
      });

      const conflicts = Object.values(grouped).filter((g) => g.chapters.length > 1);
      const defaultChoices = {};
      conflicts.forEach((g) => {
        defaultChoices[g.key] = g.chapters[0].id;
      });

      setMergeConflicts(conflicts);
      setChapterChoices(defaultChoices);
      // Initialiser l'ordre des livres et des chapitres
      setOrderedBooks(data.books || []);
      setOrderedChapters(allChapters);
    } catch (error) {
      console.error('Erreur lors de la prévisualisation de la fusion:', error);
      const msg = error?.response?.data?.error || error?.message || t('documents.merge.previewErrorFallback');
      setMergePreviewError(msg);
    } finally {
      setMergePreviewLoading(false);
    }
  };

  // Déplacer un chapitre vers le haut dans la liste
  const moveChapterUp = (index) => {
    if (index <= 0) return;
    setOrderedChapters((prev) => {
      const newList = [...prev];
      [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
      return newList;
    });
  };

  // Déplacer un chapitre vers le bas dans la liste
  const moveChapterDown = (index) => {
    setOrderedChapters((prev) => {
      if (index >= prev.length - 1) return prev;
      const newList = [...prev];
      [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
      return newList;
    });
  };

  // Déplacer un livre vers le haut et reconstruire l'ordre des chapitres
  const moveBookUp = (index) => {
    if (index <= 0) return;
    setOrderedBooks((prev) => {
      const newBooks = [...prev];
      [newBooks[index - 1], newBooks[index]] = [newBooks[index], newBooks[index - 1]];
      // Reconstruire l'ordre des chapitres selon le nouvel ordre des livres
      rebuildChaptersFromBooks(newBooks);
      return newBooks;
    });
  };

  // Déplacer un livre vers le bas et reconstruire l'ordre des chapitres
  const moveBookDown = (index) => {
    setOrderedBooks((prev) => {
      if (index >= prev.length - 1) return prev;
      const newBooks = [...prev];
      [newBooks[index], newBooks[index + 1]] = [newBooks[index + 1], newBooks[index]];
      // Reconstruire l'ordre des chapitres selon le nouvel ordre des livres
      rebuildChaptersFromBooks(newBooks);
      return newBooks;
    });
  };

  // Reconstruire l'ordre des chapitres selon l'ordre des livres
  const rebuildChaptersFromBooks = (books) => {
    const newChapters = [];
    books.forEach((book) => {
      (book.chapters || []).forEach((ch) => {
        newChapters.push({
          id: ch.id,
          title: ch.title || '',
          bookId: book.id,
          bookTitle: book.title || '',
        });
      });
    });
    setOrderedChapters(newChapters);
  };

  const handleCloseMergeDialog = () => {
    setShowMergeDialog(false);
    setMergeError(null);
    setMergePreview(null);
    setMergePreviewError(null);
    setMergeConflicts([]);
    setChapterChoices({});
    setOrderedChapters([]);
    setOrderedBooks([]);
  };

  const refreshDocuments = async () => {
    setIsLoading(true);
    try {
      const data = await bookService.getAllBooks({ page: 1 });
      setDocuments(data.results || []);
      setTotalCount(data.count || 0);
      setNextPage(data.next);
      setPreviousPage(data.previous);
      const pageSize = 10;
      setTotalPages(Math.ceil((data.count || 0) / pageSize));
      setCurrentPage(1);
    } catch (error) {
      console.error('Erreur lors du rechargement des documents:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmMerge = async () => {
    if (selectedIds.length < 2 || !mergeTargetId || !mergeTitleId || !mergeCoverId) {
      return;
    }
    if (!mergePreview || !Array.isArray(mergePreview.books)) {
      setMergeError('Prévisualisation indisponible, veuillez réessayer.');
      return;
    }

    // Construire un set des chapitres en doublon (pour filtrage)
    const grouped = {};
    orderedChapters.forEach((ch) => {
      const keyBase = (ch.title || '').trim().toLowerCase();
      const key = keyBase || `__no_title__${ch.id}`;
      if (!grouped[key]) {
        grouped[key] = { key, chapters: [] };
      }
      grouped[key].chapters.push(ch);
    });

    // Déterminer quels chapitres inclure (en respectant l'ordre de orderedChapters)
    const excludedIds = new Set();
    Object.values(grouped).forEach((g) => {
      if (g.chapters.length > 1) {
        const chosenId = chapterChoices[g.key] || g.chapters[0].id;
        g.chapters.forEach((ch) => {
          if (ch.id !== chosenId) {
            excludedIds.add(ch.id);
          }
        });
      }
    });

    // Utiliser l'ordre de orderedChapters, en excluant les chapitres non sélectionnés
    const includedChapterIds = orderedChapters
      .filter((ch) => !excludedIds.has(ch.id))
      .map((ch) => ch.id);

    try {
      setIsMerging(true);
      setMergeError(null);
      await bookService.mergeMultipleCreate({
        bookIds: selectedIds,
        metaFromBookId: mergeTargetId,
        keepTitleFromId: mergeTitleId,
        keepCoverFromId: mergeCoverId,
        includedChapterIds,
      });
      await refreshDocuments();
      setSelectedIds([]);
      setShowMergeDialog(false);
      setMergePreview(null);
      setMergePreviewError(null);
      setMergeConflicts([]);
      setChapterChoices({});
      setOrderedChapters([]);
      setOrderedBooks([]);
    } catch (error) {
      console.error('Erreur lors de la fusion des documents:', error);
      const msg = error?.response?.data?.error || error?.message || t('documents.merge.mergeError');
      setMergeError(msg);
    } finally {
      setIsMerging(false);
    }
  };

  // Gestionnaires d'événements
  const handleViewDocument = (docId) => {
    navigate(`/documents/${docId}`);
  };


  const handleDeleteDocument = async (docId) => {
    const confirmed = await new Promise((resolve) => {
      toast((toastObj) => (
        <div className="flex flex-col gap-3">
          <div>
            <div className="font-semibold text-gray-900 mb-1">{t('documents.confirmDelete.title')}</div>
            <div className="text-sm text-gray-600">{t('documents.confirmDelete.subtitle')}</div>
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => { toast.dismiss(toastObj.id); resolve(false); }}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              {t('documents.buttons.cancel')}
            </button>
            <button
              onClick={() => { toast.dismiss(toastObj.id); resolve(true); }}
              className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
            >
              {t('documents.buttons.delete')}
            </button>
          </div>
        </div>
      ), {
        duration: Infinity,
        position: 'top-center',
        style: { maxWidth: '500px', padding: '16px' },
      });
    });
    
    if (!confirmed) return;
    
    const loadingToast = toast.loading(t('documents.toasts.deleteLoading'), { position: 'top-right' });
    
    try {
      await bookService.deleteBook(docId);
      // Mettre à jour la liste des documents après suppression
      setDocuments(documents.filter(doc => doc.id !== docId));
      
      toast.success(t('documents.toasts.deleteSuccess'), { id: loadingToast, duration: 3000, position: 'top-right' });
    } catch (error) {
      console.error('Erreur lors de la suppression du document:', error);
      toast.error(t('documents.toasts.deleteError'), { id: loadingToast, duration: 4000, position: 'top-right' });
    }
  };

  const handleDownloadDocument = async (docOrPdfUrl) => {
    try {
      // Récupérer l'URL du PDF à partir d'une chaîne ou d'un objet document
      let pdfUrl = '';
      if (typeof docOrPdfUrl === 'string') {
        pdfUrl = docOrPdfUrl || '';
      } else if (docOrPdfUrl && typeof docOrPdfUrl === 'object') {
        pdfUrl = docOrPdfUrl.pdf_url || docOrPdfUrl.pdf || '';
      }

      if (!pdfUrl) {
        toast.error(t('documents.toasts.downloadNoLink'), { position: 'top-right', duration: 3000 });
        return;
      }

      // Construire l'URL complète du PDF
      const fullUrl = pdfUrl.startsWith('http') ? pdfUrl : `${pdfUrl}`;
      
      // Créer un lien temporaire pour le téléchargement
      const link = document.createElement('a');
      link.href = fullUrl;
      link.target = '_blank';
      link.download = (pdfUrl.split('/').pop() || 'document.pdf');
      
      // Ajouter le lien au document, cliquer dessus, puis le supprimer
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('Téléchargement du PDF initié:', fullUrl);
      toast.success(t('documents.toasts.downloadStarted'), { position: 'top-right', duration: 3000 });
    } catch (error) {
      console.error('Erreur lors du téléchargement du document:', error);
      toast.error(t('documents.toasts.downloadError'), { position: 'top-right', duration: 4000 });
    }
  };

  const handleUploadNew = () => {
    navigate('/upload');
  };

  const handleTogglePublished = async (doc) => {
    try {
      const updated = await bookService.updateBook(doc.id, { published: !doc.published });
      const newPublished = typeof updated?.published === 'boolean' ? updated.published : !doc.published;
      setDocuments((prev) => prev.map((d) => (d.id === doc.id ? { ...d, published: newPublished } : d)));
    } catch (error) {
      console.error('Erreur lors du changement de statut de publication:', error);
      // On ne bloque pas l’UI, mais on pourrait afficher une notif ici
    }
  };

  return (
    <div className="flex-1 p-4 sm:p-6 md:p-8 overflow-y-auto">
      <Toaster />
      <div className="max-w-7xl mx-auto">
        {/* En-tête avec titre et boutons (Web) */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{t('documents.title')}</h1>
              <p className="text-gray-600 text-base sm:text-lg">{t('documents.subtitle')}</p>
            </div>
            
            {/* Boutons Nouveau et Fusionner (Web uniquement) */}
            {isAdmin && (
              <div className="hidden md:flex items-center gap-2">
                <Button 
                  variant="primary" 
                  className="flex items-center gap-2"
                  onClick={handleUploadNew}
                >
                  <Plus size={18} />
                  <span>{t('documents.buttons.newDocument')}</span>
                </Button>
                <Button
                  variant="accent"
                  disabled={selectedIds.length < 2}
                  onClick={handleOpenMergeDialog}
                >
                  {t('documents.buttons.merge')} {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Barre de recherche avec boutons Documentation et Actualiser (Web) */}
        <div className="mb-4 sm:mb-6">
          {/* Mobile : Fusionner + Actualiser (seulement pour admin/manager) */}
          {!isEmployee && (
            <div className="flex md:hidden gap-2 mb-3">
              {isAdmin && (
                <Button
                  variant="accent"
                  disabled={selectedIds.length < 2}
                  onClick={handleOpenMergeDialog}
                  className="flex items-center justify-center gap-2 flex-1 px-6"
                >
                  {t('documents.buttons.merge')}
                </Button>
              )}
              <Button 
                variant="outline" 
                className="flex items-center justify-center gap-2 flex-1 px-4"
                onClick={handleRefresh}
                disabled={isLoading}
              >
                <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
              </Button>
            </div>
          )}

          {/* Mobile : Nouveau + Documentation (Docs seulement pour admin/manager) */}
          <div className="flex md:hidden gap-2 mb-3">
            {isAdmin && (
              <Button 
                variant="primary" 
                className="flex items-center justify-center gap-2 flex-1 px-6"
                onClick={handleUploadNew}
              >
                <Plus size={18} />
                <span>{t('documents.buttons.newShort')}</span>
              </Button>
            )}
            {(isAdmin || isManager) && (
              <Button 
                variant="outline" 
                className="flex items-center justify-center gap-2 flex-1 px-4"
                onClick={() => navigate('/tags-documentation')}
              >
                <BookOpen size={18} />
                <span>{t('documents.buttons.docs')}</span>
              </Button>
            )}
          </div>

          {/* Barre de recherche (Mobile et Web) */}
          <div className={`w-full ${isEmployee ? 'flex items-center gap-2' : 'md:flex md:items-center md:gap-3'}`}>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: isEmployee ? 'auto' : '100%', ...(isEmployee ? { flex: 1 } : {}), border: '1px solid #d1d5db', borderRadius: '9999px', backgroundColor: 'white', padding: '6px 12px' }}>
              <Search style={{ width: '16px', height: '16px', color: '#9ca3af', flexShrink: 0 }} />
              <input
                type="text"
                style={{ flex: 1, margin: '0 8px', backgroundColor: 'transparent', border: 'none', outline: 'none', fontSize: '14px' }}
                placeholder={t('documents.search.placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {isSearching ? (
                <button 
                  onClick={handleResetSearch}
                  style={{ color: '#ef4444', flexShrink: 0 }}
                  title={t('documents.search.resetTitle')}
                >
                  <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              ) : (
                <button style={{ color: '#8b5cf6', flexShrink: 0 }}>
                  <Filter style={{ width: '16px', height: '16px' }} />
                </button>
              )}
            </div>
            
            {/* Boutons Documentation (admin/manager) et Actualiser (tous) */}
            <div className={`${isEmployee ? 'flex items-center gap-2 ml-2' : 'hidden sm:flex items-center gap-2'}`}>
              {(isAdmin || isManager) && (
                <Button 
                  variant="outline" 
                  className="flex items-center gap-2"
                  onClick={() => navigate('/tags-documentation')}
                >
                  <BookOpen size={18} />
                  <span>{t('documents.buttons.documentation')}</span>
                </Button>
              )}
              <Button 
                variant="outline" 
                className="flex items-center gap-2"
                onClick={handleRefresh}
                disabled={isLoading}
              >
                <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
                {!isEmployee && <span>{t('documents.buttons.refresh')}</span>}
              </Button>
            </div>
          </div>
          
          {/* Indicateur de recherche active */}
          {isSearching && (
            <div className="mt-3 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {t('documents.search.resultsFor')} "{searchQuery}" : {totalCount} {totalCount !== 1 ? t('documents.search.documentPlural') : t('documents.search.documentSingular')}
              </div>
              <button
                onClick={handleResetSearch}
                className="text-sm text-primary hover:text-primary-700 font-medium"
              >
                {t('documents.search.seeAll')}
              </button>
            </div>
          )}
        </div>

        {/* Info sélection Web */}
        {isAdmin && selectedIds.length > 0 && (
          <div className="hidden md:block mb-4 text-sm text-gray-600">
            {selectedIds.length} {selectedIds.length > 1 ? t('documents.selection.documentPlural') : t('documents.selection.documentSingular')}
            <button
              onClick={clearSelection}
              className="ml-2 text-primary hover:underline"
            >
              {t('documents.selection.clear')}
            </button>
          </div>
        )}

        {/* Info sélection mobile */}
        {isAdmin && selectedIds.length > 0 && (
          <div className="md:hidden mb-4 text-sm text-gray-600 text-center">
            {selectedIds.length} {selectedIds.length > 1 ? t('documents.selection.documentPlural') : t('documents.selection.documentSingular')}
            <button
              onClick={clearSelection}
              className="ml-2 text-primary hover:underline"
            >
              {t('documents.selection.clearShort')}
            </button>
          </div>
        )}

        {/* Liste des documents */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : documents.length > 0 ? (
          <>
            {/* Statistiques des documents */}
            {/* <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-primary-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-primary">{documents.length}</div>
                <div className="text-sm text-gray-600">Total documents</div>
              </div>
              <div className="bg-success-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-success">
                  {documents.reduce((sum, doc) => sum + (doc.chapters_count || 0), 0)}
                </div>
                <div className="text-sm text-gray-600">Chapitres totaux</div>
              </div>
              <div className="bg-accent-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-accent">
                  {Math.floor(documents.reduce((sum, doc) => sum + (doc.file_size || 0), 0) / 1024 / 1024)}
                </div>
                <div className="text-sm text-gray-600">MB utilisés</div>
              </div>
              <div className="bg-warning-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-warning">
                  {new Date().toLocaleDateString('fr-FR')}
                </div>
                <div className="text-sm text-gray-600">Dernière mise à jour</div>
              </div>
            </div> */}
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {documents.map((doc) => (
                <DocumentCard
                  key={doc.id}
                  title={doc.title}
                  chapters_count={doc.chapters_count || 0}
                  document={doc}
                  date={doc.created_at ? new Date(doc.created_at).toLocaleDateString(dateLocale) : ''}
                  onView={() => handleViewDocument(doc.id)}
                  onDownload={() => handleDownloadDocument(doc.pdf_url)}
                  onDelete={() => handleDeleteDocument(doc.id)}
                  canDownload={isAdmin}
                  published={doc.published}
                  canTogglePublished={isAdmin}
                  onTogglePublished={() => handleTogglePublished(doc)}
                  canDelete={isAdmin}
                  selectable={isAdmin}
                  selected={selectedIds.includes(doc.id)}
                  onToggleSelect={() => toggleSelectDocument(doc.id)}
                />
              ))}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-wrap justify-center items-center gap-2 mt-6 sm:mt-8">
                <Button
                  onClick={handlePreviousPage}
                  disabled={!previousPage}
                  variant="outline"
                  className="flex items-center space-x-2"
                >
                  <span>{t('documents.buttons.previous')}</span>
                </Button>
                
                <div className="flex items-center space-x-2">
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        className="w-10 h-10"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                <Button
                  onClick={handleNextPage}
                  disabled={!nextPage}
                  variant="outline"
                  className="flex items-center space-x-2"
                >
                  <span>{t('documents.buttons.next')}</span>
                </Button>
              </div>
            )}
            
            {/* Information sur la pagination */}
            {totalCount > 0 && (
              <div className="mt-4 text-center text-sm text-gray-600">
                {t('documents.pagination.displaying')} {documents.length} {t('documents.pagination.of')} {totalCount} {t('documents.pagination.documents')}
                {totalPages > 1 && ` (${t('documents.pagination.page')} ${currentPage} ${t('documents.pagination.of')} ${totalPages})`}
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center text-gray-400 mb-4">
              <Search size={32} />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">{t('documents.empty.title')}</h3>
            <p className="text-gray-500">{t('documents.empty.subtitle')}</p>
          </div>
        )}

        {showMergeDialog && selectedDocs.length >= 2 && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-lg w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('documents.merge.title')}</h2>
              <p className="text-sm text-gray-600 mb-4">
                {t('documents.merge.subtitle')}
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('documents.merge.targetBookLabel')}</label>
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    value={mergeTargetId || ''}
                    onChange={(e) => setMergeTargetId(Number(e.target.value))}
                  >
                    {selectedDocs.map((doc) => (
                      <option key={doc.id} value={doc.id}>
                        {doc.title} (ID {doc.id})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('documents.merge.titleLabel')}</label>
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    value={mergeTitleId || ''}
                    onChange={(e) => setMergeTitleId(Number(e.target.value))}
                  >
                    {selectedDocs.map((doc) => (
                      <option key={doc.id} value={doc.id}>
                        {doc.title} (ID {doc.id})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('documents.merge.coverLabel')}</label>
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    value={mergeCoverId || ''}
                    onChange={(e) => setMergeCoverId(Number(e.target.value))}
                  >
                    {selectedDocs.map((doc) => (
                      <option key={doc.id} value={doc.id}>
                        {doc.title} (ID {doc.id})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Section de réordonnancement des livres */}
                <div className="border-t border-gray-200 pt-4 mt-2">
                  <h3 className="text-sm font-medium text-gray-800 mb-2">{t('documents.merge.booksOrderTitle')}</h3>
                  <p className="text-xs text-gray-500 mb-2">
                    {t('documents.merge.booksOrderHelp')}
                  </p>
                  {mergePreviewLoading ? (
                    <div className="text-sm text-gray-500">{t('documents.merge.booksLoading')}</div>
                  ) : mergePreviewError ? (
                    <div className="text-sm text-red-600">{mergePreviewError}</div>
                  ) : (
                    <div className="space-y-1 border border-gray-200 rounded-md p-2">
                      {orderedBooks.map((book, index) => (
                        <div
                          key={book.id}
                          className="flex items-center gap-2 p-2 bg-blue-50 rounded-md hover:bg-blue-100"
                        >
                          <div className="flex flex-col">
                            <button
                              type="button"
                              onClick={() => moveBookUp(index)}
                              disabled={index === 0}
                              className={`p-0.5 rounded ${index === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-200'}`}
                              title="Monter"
                            >
                              <ChevronUp size={16} />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveBookDown(index)}
                              disabled={index === orderedBooks.length - 1}
                              className={`p-0.5 rounded ${index === orderedBooks.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-200'}`}
                              title="Descendre"
                            >
                              <ChevronDown size={16} />
                            </button>
                          </div>
                          <span className="text-sm font-medium text-blue-800 w-6">{index + 1}.</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-blue-900 truncate">{book.title}</div>
                            <div className="text-xs text-blue-600">{book.chapters?.length || 0} chapitre(s)</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Section de réordonnancement des chapitres */}
                <div className="border-t border-gray-200 pt-4 mt-2">
                  <h3 className="text-sm font-medium text-gray-800 mb-2">{t('documents.merge.chaptersOrderTitle')}</h3>
                  <p className="text-xs text-gray-500 mb-2">
                    {t('documents.merge.chaptersOrderHelp')}
                  </p>
                  {mergePreviewLoading ? (
                    <div className="text-sm text-gray-500">{t('documents.merge.chaptersLoading')}</div>
                  ) : mergePreviewError ? (
                    <div className="text-sm text-red-600">{mergePreviewError}</div>
                  ) : (
                    <div className="space-y-1 max-h-48 overflow-y-auto pr-1 border border-gray-200 rounded-md p-2">
                      {orderedChapters.map((ch, index) => (
                        <div
                          key={ch.id}
                          className="flex items-center gap-2 p-2 bg-gray-50 rounded-md hover:bg-gray-100"
                        >
                          <div className="flex flex-col">
                            <button
                              type="button"
                              onClick={() => moveChapterUp(index)}
                              disabled={index === 0}
                              className={`p-0.5 rounded ${index === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                              title="Monter"
                            >
                              <ChevronUp size={16} />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveChapterDown(index)}
                              disabled={index === orderedChapters.length - 1}
                              className={`p-0.5 rounded ${index === orderedChapters.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                              title="Descendre"
                            >
                              <ChevronDown size={16} />
                            </button>
                          </div>
                          <span className="text-xs text-gray-400 w-6">{index + 1}.</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-gray-800 truncate">{ch.title || '(Sans titre)'}</div>
                            <div className="text-xs text-gray-500 truncate">{ch.bookTitle}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Section de gestion des doublons */}
                <div className="border-t border-gray-200 pt-4 mt-2">
                  <h3 className="text-sm font-medium text-gray-800 mb-2">{t('documents.merge.duplicatesTitle')}</h3>
                  {mergePreviewLoading && (
                    <div className="text-sm text-gray-500">{t('documents.merge.duplicatesLoading')}</div>
                  )}
                  {!mergePreviewLoading && mergePreviewError && (
                    <div className="text-sm text-red-600">{mergePreviewError}</div>
                  )}
                  {!mergePreviewLoading && !mergePreviewError && (
                    <>
                      {mergeConflicts.length === 0 ? (
                        <div className="text-sm text-gray-500">
                          {t('documents.merge.noDuplicates')}
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                          {mergeConflicts.map((group) => (
                            <div key={group.key} className="border border-gray-200 rounded-md p-2">
                              <div className="text-sm font-medium text-gray-800 mb-1">
                                {t('documents.merge.chapterLabel')} {group.title}
                              </div>
                              <div className="space-y-1">
                                {group.chapters.map((ch) => (
                                  <label
                                    key={ch.id}
                                    className="flex items-center gap-2 text-sm text-gray-700"
                                  >
                                    <input
                                      type="radio"
                                      name={`chapter-conf-${group.key}`}
                                      value={ch.id}
                                      checked={chapterChoices[group.key] === ch.id}
                                      onChange={() =>
                                        setChapterChoices((prev) => ({
                                          ...prev,
                                          [group.key]: ch.id,
                                        }))
                                      }
                                    />
                                    <span>
                                      {(ch.bookTitle || t('documents.merge.bookLabelFallback'))} – {t('documents.merge.chapterIdLabel')} {ch.id}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {mergeError && (
                  <div className="text-sm text-red-600 mt-1">{mergeError}</div>
                )}
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={handleCloseMergeDialog}
                  disabled={isMerging}
                >
                  {t('documents.buttons.cancel')}
                </Button>
                <Button
                  variant="primary"
                  onClick={handleConfirmMerge}
                  disabled={isMerging}
                >
                  {isMerging ? t('documents.buttons.merging') : t('documents.buttons.mergeNow')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Documents;
